import { useRef, useState, type MouseEvent } from 'react';
import { useGT } from 'gt-react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Action, Subject } from '@erp/authz';
import type { AgentPageResponse, TaskPageResponse } from '@erp/rpc';
import { useCan } from '@/hooks/useCan';
import {
  useAgentPages,
  useArchiveAgentPage,
  useUpdateAgentPage,
} from '@/api/rpc/hooks/agent-pages/useAgentPages';
import {
  useArchiveTaskPage,
  useTaskPages,
  useUpdateTaskPage,
} from '@/api/rpc/hooks/tasks/useTaskPages';
import {
  DropdownMenu,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import {
  AgentPageSidebarDialogs,
  TaskPageSidebarDialogs,
} from '@/components/sidenav/SidebarPageDialogs';
import {
  SidebarPageEditItems,
  SidebarPageRemoveItem,
} from '@/components/sidenav/SidebarPageMenuActions';
import { toast } from '@/components/ui/toast';
import type {
  RouterNavEntry,
  SidebarNavGroup,
} from '@/routing/nav-from-router';
import {
  configurablePagesInOrder,
  isViewingSidebarPage,
  manageableSidebarPage,
  pageMoveUpdates,
  sidebarPageEntryId,
  sidebarPageGrants,
  sidebarPageMoveAvailability,
  sidebarPagePath,
  type OrderedSidebarPage,
  type PageSortUpdate,
  type SidebarPageGrants,
  type SidebarPageKind,
} from '@/routing/sidebar-page-order';
import { endMove, tryBeginMove } from '@/routing/sidebar-page-move-gate';
import {
  draftFromAgentPage,
  draftToCreateInput as agentDraftToCreateInput,
  draftToUpdateInput as agentDraftToUpdateInput,
} from '@/pages/authenticated/settings/organization/components/cards/agent-pages/agentPageAdmin';
import {
  draftFromTaskPage,
  draftToCreateInput,
  draftToUpdateInput,
  mutationErrorMessage,
} from '@/pages/authenticated/settings/organization/components/cards/task-pages/taskPageAdmin';
import { useEntityEditor } from '@/pages/authenticated/settings/organization/components/cards/shared/useEntityEditor';

type MenuTarget = {
  x: number;
  y: number;
  kind: SidebarPageKind;
  id: string;
  groupId: string;
};

type PageIndex = {
  grants: SidebarPageGrants;
  taskById: ReadonlyMap<string, TaskPageResponse>;
  agentById: ReadonlyMap<string, AgentPageResponse>;
  sortOrderByEntryId: ReadonlyMap<string, number>;
  knownEntryIds: ReadonlySet<string>;
};

/**
 * Right-click menu for an org-configured task page or agent page: move it
 * among the pages in its group, open the settings editor, or remove it.
 * Built-in routes get no menu. Update reorders and edits; Delete removes.
 */
export function useSidebarPageMenu(groups: readonly SidebarNavGroup[]) {
  const gt = useGT();
  const catalog = useSidebarPageCatalog();
  const taskPages = useTaskPageSidebarActions();
  const agentPages = useAgentPageSidebarActions();
  const [target, setTarget] = useState<MenuTarget | null>(null);
  const access = target
    ? sidebarPageGrants(target.kind, catalog.grants)
    : NO_ACCESS;
  const pages = pagesForTarget(groups, target, catalog.sortOrderByEntryId);
  const moves = target ? sidebarPageMoveAvailability(pages, target) : NO_MOVES;
  const { moving, run: runMove } = useMoveInFlight();

  function openAt(
    event: MouseEvent,
    entry: RouterNavEntry,
    group: SidebarNavGroup,
  ) {
    const ref = manageableSidebarPage(
      entry.id,
      catalog.grants,
      catalog.knownEntryIds,
    );
    if (!ref) return;
    event.preventDefault();
    setTarget({
      ...ref,
      x: event.clientX,
      y: event.clientY,
      groupId: group.id,
    });
  }

  function move(direction: 'up' | 'down') {
    if (!target || !access.canUpdate) return;
    const updates = pageMoveUpdates(pages, target, direction);
    if (!updates || updates.length === 0) return;
    runMove(() =>
      savePageOrder(updates, applySort, movedToast(gt), failedMoveToast(gt)),
    );
  }

  function applySort(update: PageSortUpdate) {
    const input = { id: update.id, sortOrder: update.sortOrder };
    return update.kind === 'task'
      ? taskPages.update(input)
      : agentPages.update(input);
  }

  function changeSettings() {
    if (!target || !access.canUpdate) return;
    openPageEditor(target, catalog, taskPages, agentPages);
  }

  function removePage() {
    if (!target || !access.canDelete) return;
    askToRemovePage(target, catalog, taskPages, agentPages);
  }

  const anchor = target
    ? { getBoundingClientRect: () => new DOMRect(target.x, target.y, 0, 0) }
    : undefined;

  const menu = (
    <>
      <DropdownMenu
        open={target !== null}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
      >
        <DropdownMenuContent
          anchor={anchor}
          side="bottom"
          align="start"
          sideOffset={0}
          data-testid="sidebar-page-menu"
        >
          {target && access.canUpdate && (
            <SidebarPageEditItems
              canMoveUp={moves.up}
              canMoveDown={moves.down}
              moving={moving}
              onMoveUp={() => move('up')}
              onMoveDown={() => move('down')}
              onChangeSettings={changeSettings}
            />
          )}
          {target && access.canDelete && (
            <SidebarPageRemoveItem
              separated={access.canUpdate}
              moving={moving}
              onRemove={removePage}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {taskPages.dialogs}
      {agentPages.dialogs}
    </>
  );

  return { openAt, menu };
}

const NO_ACCESS = { canUpdate: false, canDelete: false };
const NO_MOVES = { up: false, down: false };

/**
 * One guard for the whole reorder. A move writes two pages through the same
 * `useMutation`, and that hook's `isPending` follows only the latest call, so
 * it can drop while the other update is still running.
 */
function useMoveInFlight() {
  const inFlight = useRef(false);
  const [moving, setMoving] = useState(false);

  function run(work: () => Promise<void>): void {
    if (!tryBeginMove(inFlight)) return;
    setMoving(true);
    void work().finally(() => {
      endMove(inFlight);
      setMoving(false);
    });
  }

  return { moving, run };
}

function pagesForTarget(
  groups: readonly SidebarNavGroup[],
  target: MenuTarget | null,
  sortOrderByEntryId: ReadonlyMap<string, number>,
): OrderedSidebarPage[] {
  if (!target) return [];
  const group = groups.find((item) => item.id === target.groupId);
  return configurablePagesInOrder(
    group?.items.map((item) => item.id) ?? [],
    sortOrderByEntryId,
  );
}

function openPageEditor(
  target: MenuTarget,
  catalog: PageIndex,
  taskPages: { openEditor: (page: TaskPageResponse) => void },
  agentPages: { openEditor: (page: AgentPageResponse) => void },
) {
  if (target.kind === 'task') {
    const page = catalog.taskById.get(target.id);
    if (page) taskPages.openEditor(page);
    return;
  }
  const page = catalog.agentById.get(target.id);
  if (page) agentPages.openEditor(page);
}

function askToRemovePage(
  target: MenuTarget,
  catalog: PageIndex,
  taskPages: { requestRemove: (page: TaskPageResponse) => void },
  agentPages: { requestRemove: (page: AgentPageResponse) => void },
) {
  if (target.kind === 'task') {
    const page = catalog.taskById.get(target.id);
    if (page) taskPages.requestRemove(page);
    return;
  }
  const page = catalog.agentById.get(target.id);
  if (page) agentPages.requestRemove(page);
}

async function savePageOrder(
  updates: readonly PageSortUpdate[],
  apply: (update: PageSortUpdate) => Promise<unknown>,
  moved: string,
  failed: string,
) {
  try {
    await Promise.all(updates.map((update) => apply(update)));
    toast.success(moved);
  } catch (error) {
    toast.error(mutationErrorMessage(error, failed));
  }
}

function movedToast(gt: ReturnType<typeof useGT>): string {
  return gt('Moved the page', {
    context: 'toast after moving a sidebar page up or down',
  });
}

function failedMoveToast(gt: ReturnType<typeof useGT>): string {
  return gt('Failed to move the page', {
    context: 'toast after moving a sidebar page failed',
  });
}

function useSidebarPageCatalog(): PageIndex {
  const [taskUpdate] = useCan(Action.Update, Subject.TaskPage);
  const [taskDelete] = useCan(Action.Delete, Subject.TaskPage);
  const [agentUpdate] = useCan(Action.Update, Subject.BackgroundAgent);
  const [agentDelete] = useCan(Action.Delete, Subject.BackgroundAgent);
  const grants = { taskUpdate, taskDelete, agentUpdate, agentDelete };
  const { data: taskPages } = useTaskPages({
    enabled: taskUpdate || taskDelete,
  });
  const { data: agentPages } = useAgentPages({
    enabled: agentUpdate || agentDelete,
  });
  return indexPages(grants, taskPages, agentPages);
}

function indexPages(
  grants: SidebarPageGrants,
  taskPages: readonly TaskPageResponse[] | undefined,
  agentPages: readonly AgentPageResponse[] | undefined,
): PageIndex {
  const taskById = new Map<string, TaskPageResponse>();
  const agentById = new Map<string, AgentPageResponse>();
  const sortOrderByEntryId = new Map<string, number>();
  const knownEntryIds = new Set<string>();
  for (const page of taskPages ?? []) {
    rememberPage('task', page, taskById, sortOrderByEntryId, knownEntryIds);
  }
  for (const page of agentPages ?? []) {
    rememberPage('agent', page, agentById, sortOrderByEntryId, knownEntryIds);
  }
  return { grants, taskById, agentById, sortOrderByEntryId, knownEntryIds };
}

function rememberPage<Page extends { id: string; sortOrder: number }>(
  kind: SidebarPageKind,
  page: Page,
  byId: Map<string, Page>,
  sortOrderByEntryId: Map<string, number>,
  knownEntryIds: Set<string>,
) {
  const entryId = sidebarPageEntryId({ kind, id: page.id });
  byId.set(page.id, page);
  sortOrderByEntryId.set(entryId, page.sortOrder);
  knownEntryIds.add(entryId);
}

type SortInput = { id: string; sortOrder: number };

function useTaskPageSidebarActions() {
  const gt = useGT();
  const leaveIfViewing = useLeavePage();
  const updateTask = useUpdateTaskPage();
  const archiveTask = useArchiveTaskPage();
  const editor = useEntityEditor({
    toCreateInput: draftToCreateInput,
    toUpdateInput: draftToUpdateInput,
    create: skipCreate,
    update: (input: Parameters<typeof updateTask.mutateAsync>[0]) =>
      updateTask.mutateAsync(input),
    archive: (page: TaskPageResponse) =>
      archiveTask.mutateAsync({ id: page.id }).then((result) => {
        leaveIfViewing('task', page.id);
        return result;
      }),
    messages: taskPageToasts(gt),
  });
  return {
    update: (input: SortInput) => updateTask.mutateAsync(input),
    openEditor: (page: TaskPageResponse) =>
      editor.openEditor(draftFromTaskPage(page)),
    requestRemove: editor.setArchiveTarget,
    dialogs: (
      <TaskPageSidebarDialogs
        draft={editor.draft}
        initialDraft={editor.initialDraft}
        archiveName={editor.archiveTarget?.name ?? ''}
        archiveOpen={editor.archiveTarget !== null}
        isSaving={updateTask.isPending}
        isRemoving={archiveTask.isPending}
        onChange={editor.setDraft}
        onClose={editor.closeEditor}
        onSave={editor.save}
        onArchiveClose={() => editor.setArchiveTarget(null)}
        onArchive={editor.archive}
      />
    ),
  };
}

function useAgentPageSidebarActions() {
  const gt = useGT();
  const leaveIfViewing = useLeavePage();
  const updateAgent = useUpdateAgentPage();
  const archiveAgent = useArchiveAgentPage();
  const editor = useEntityEditor({
    toCreateInput: agentDraftToCreateInput,
    toUpdateInput: agentDraftToUpdateInput,
    create: skipCreate,
    update: (input: Parameters<typeof updateAgent.mutateAsync>[0]) =>
      updateAgent.mutateAsync(input),
    archive: (page: AgentPageResponse) =>
      archiveAgent.mutateAsync({ id: page.id }).then((result) => {
        leaveIfViewing('agent', page.id);
        return result;
      }),
    messages: agentPageToasts(gt),
  });
  return {
    update: (input: SortInput) => updateAgent.mutateAsync(input),
    openEditor: (page: AgentPageResponse) =>
      editor.openEditor(draftFromAgentPage(page)),
    requestRemove: editor.setArchiveTarget,
    dialogs: (
      <AgentPageSidebarDialogs
        draft={editor.draft}
        initialDraft={editor.initialDraft}
        archiveName={editor.archiveTarget?.name ?? ''}
        archiveOpen={editor.archiveTarget !== null}
        isSaving={updateAgent.isPending}
        isRemoving={archiveAgent.isPending}
        onChange={editor.setDraft}
        onClose={editor.closeEditor}
        onSave={editor.save}
        onArchiveClose={() => editor.setArchiveTarget(null)}
        onArchive={editor.archive}
      />
    ),
  };
}

function useLeavePage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return function leaveIfViewing(kind: SidebarPageKind, id: string) {
    if (isViewingSidebarPage(pathname, sidebarPagePath(kind, id))) {
      void navigate({ to: '/' });
    }
  };
}

async function skipCreate(): Promise<void> {
  return undefined;
}

function taskPageToasts(gt: ReturnType<typeof useGT>) {
  return {
    saved: gt('Task page saved', {
      context: 'toast after saving a task page from the sidebar',
    }),
    saveFailed: gt('Failed to save the task page', {
      context: 'toast after a task page save from the sidebar failed',
    }),
    archived: gt('Removed the page from the sidebar', {
      context: 'toast after removing a task page from the sidebar',
    }),
    archiveFailed: gt('Failed to remove the page', {
      context: 'toast after removing a task page from the sidebar failed',
    }),
  };
}

function agentPageToasts(gt: ReturnType<typeof useGT>) {
  return {
    saved: gt('Agent page saved', {
      context: 'toast after saving an agent page from the sidebar',
    }),
    saveFailed: gt('Failed to save the agent page', {
      context: 'toast after an agent page save from the sidebar failed',
    }),
    archived: gt('Removed the page from the sidebar', {
      context: 'toast after removing an agent page from the sidebar',
    }),
    archiveFailed: gt('Failed to remove the page', {
      context: 'toast after removing an agent page from the sidebar failed',
    }),
  };
}
